import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import userService from '../services/userService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => authService.getUser());
  const [loading, setLoading] = useState(true);

  // On mount, verify token is still valid by fetching profile
  useEffect(() => {
    const verify = async () => {
      if (!authService.isAuthenticated()) {
        setLoading(false);
        return;
      }
      try {
        const profile = await userService.getProfile();
        setUser(profile);
        localStorage.setItem('user', JSON.stringify(profile));
      } catch {
        authService.logout();
        setUser(null);
      }
      setLoading(false);
    };
    verify();
  }, []);

  const loginWithIdentifier = useCallback(async (identifier, password) => {
    const { user: userData } = await authService.loginWithIdentifier(identifier, password);
    setUser(userData);
    return userData;
  }, []);

  const registerWithIdentifier = useCallback(async (identifier, password) => {
    const { user: userData } = await authService.registerWithIdentifier(identifier, password);
    setUser(userData);
    return userData;
  }, []);

  const loginWithEmail = useCallback(async (email, password) => {
    return loginWithIdentifier(email, password);
  }, [loginWithIdentifier]);

  const registerWithEmail = useCallback(async (email, password) => {
    return registerWithIdentifier(email, password);
  }, [registerWithIdentifier]);

  const loginWithGoogle = useCallback(async () => {
    const { user: userData } = await authService.loginWithGoogle();
    setUser(userData);
    return userData;
  }, []);

  const sendPhoneOtp = useCallback(async (phone, recaptchaVerifier) => {
    return authService.sendPhoneOtp(phone, recaptchaVerifier);
  }, []);

  const verifyPhoneOtp = useCallback(async (confirmationResult, code) => {
    const { user: userData } = await authService.verifyPhoneOtp(confirmationResult, code);
    setUser(userData);
    return userData;
  }, []);

  const sendEmailLink = useCallback(async (email, redirectUrl) => {
    return authService.sendEmailLink(email, redirectUrl);
  }, []);

  const completeEmailLinkSignIn = useCallback(async (email) => {
    const { user: userData } = await authService.completeEmailLinkSignIn(email);
    setUser(userData);
    return userData;
  }, []);

  const isEmailLink = useCallback((url) => authService.isEmailLink(url), []);

  const sendPasswordReset = useCallback((email) => authService.sendPasswordReset(email), []);

  const login = useCallback(async (firebaseToken, fcmToken) => {
    const { user: userData } = await authService.loginWithFirebase(firebaseToken, fcmToken);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const next = { ...prev, ...updates };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isOwner: user?.role === 'owner' || user?.role === 'both',
    isAdmin: user?.role === 'admin',
    login,
    loginWithIdentifier,
    registerWithIdentifier,
    loginWithEmail,
    registerWithEmail,
    loginWithGoogle,
    sendPhoneOtp,
    verifyPhoneOtp,
    sendEmailLink,
    completeEmailLinkSignIn,
    isEmailLink,
    sendPasswordReset,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
